/**
 * Download authenticated files from the backend as a real "Save As"
 * download (Content-Disposition: attachment). Fetching through axios
 * attaches the JWT automatically; the blob → <a download> click is the
 * only reliable cross-browser pattern regardless of CORS/popup settings.
 */
import api from "@/lib/api";

function saveBlobResponse(res, fallbackFilename) {
  let filename = fallbackFilename || "download";
  const cd = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"];
  if (cd) {
    const match = /filename="?([^"]+)"?/i.exec(cd);
    if (match && match[1]) filename = match[1];
  }
  const blob = new Blob([res.data], { type: res.data?.type || "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Defer the revoke a tick — Safari occasionally cancels the download
    // if the URL is revoked synchronously.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  return true;
}

/** Pull the FastAPI `detail` string out of an axios error whose response
 *  body is a Blob (responseType: "blob" turns JSON errors into blobs). */
async function blobErrorMessage(e) {
  const d = e?.response?.data;
  if (d instanceof Blob) {
    try {
      const j = JSON.parse(await d.text());
      if (j?.detail) return typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
    } catch {
      /* not JSON — fall through */
    }
  }
  if (e?.response?.status) return `Download failed (${e.response.status})`;
  return e?.message || "Download failed";
}

/** Download a stored upload via ``GET /api/files/{path}?download=1``. */
export async function downloadAuthedFile(path, suggestedFilename) {
  if (!path) throw new Error("No file to download");
  let res;
  try {
    res = await api.get(`/files/${path}`, {
      params: { download: 1 },
      responseType: "blob",
    });
  } catch (e) {
    throw new Error(await blobErrorMessage(e));
  }
  return saveBlobResponse(res, suggestedFilename || path.split("/").pop());
}

/** Download any authenticated API endpoint that returns a file
 *  (e.g. Excel exports). `apiPath` is relative to `/api`. */
export async function downloadApiFile(apiPath, fallbackFilename) {
  let res;
  try {
    res = await api.get(apiPath, { responseType: "blob" });
  } catch (e) {
    throw new Error(await blobErrorMessage(e));
  }
  return saveBlobResponse(res, fallbackFilename);
}
